import { Play, Tv, Film, Clock, Smartphone, ChevronRight, Check, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const goToApp = () => navigate(isAuthenticated ? "/app" : "/login");
  const channels = [
    { name: "AzTV HD", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/AzTV_logo.svg/200px-AzTV_logo.svg.png" },
    { name: "Space", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Space_TV_logo.svg/200px-Space_TV_logo.svg.png" },
    { name: "ARB", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/89/ARB_TV_logo.svg/200px-ARB_TV_logo.svg.png" },
    { name: "CBC Sport", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4f/CBC_Sport_logo.svg/200px-CBC_Sport_logo.svg.png" },
    { name: "Xəzər TV", logo: "https://upload.wikimedia.org/wikipedia/az/thumb/a/a1/X%C9%99z%C9%99r_TV.png/200px-X%C9%99z%C9%99r_TV.png" },
    { name: "Real TV", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/84/Real_TV_Azerbaijan_logo.png/200px-Real_TV_Azerbaijan_logo.png" },
  ];

  const features = [
    { icon: Tv, title: "200+ HD Kanal", desc: "Yerli və beynəlxalq kanallar" },
    { icon: Film, title: "1000+ Film", desc: "Yeni və klassik filmlər" },
    { icon: Clock, title: "7 Gün Arxiv", desc: "Buraxdığınız verilişlərə baxın" },
    { icon: Smartphone, title: "Çox Cihaz", desc: "TV, telefon, planşet, PC" },
  ];

  const plans = [
    { name: "Pulsuz Yoxla", price: "0", period: "7 gün", channels: "100", features: ["SD keyfiyyət", "1 ekranla unikal baxış", "Əsas kanallar"], isFree: true },
    { name: "Standart", price: "4", period: "ay", channels: "200", popular: true, features: ["HD keyfiyyət", "4 ekranla unikal baxış", "İdman kanalları", "VOD kitabxanası"] },
    { name: "Premium", price: "8", period: "ay", channels: "250", features: ["4K keyfiyyət", "5 ekranla unikal baxış", "Bütün kanallar", "7 gün arxiv", "VOD kitabxanası", "Smart cihaz"] },
  ];

  const testimonials = [
    { name: "Əli Məmmədov", text: "Əla xidmət! Artıq peyk antena lazım deyil, hər şey internetlə işləyir.", rating: 5 },
    { name: "Leyla Həsənova", text: "Uşaqlar üçün kanallar çoxdur, arxiv funksiyası çox rahatdır.", rating: 5 },
    { name: "Rəşad Əliyev", text: "İdman kanalları HD keyfiyyətdə, futbol izləmək həzz verir!", rating: 4 },
  ];

  return (
    <div className="min-h-screen bg-[#09090b] text-white overflow-x-hidden">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/fibernet-logo.png" alt="Fibernet TV" className="w-8 h-8 sm:w-10 sm:h-10" />
            <span className="text-lg sm:text-xl font-bold">Fibernet TV</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <a href="#features" className="hidden md:inline text-zinc-400 hover:text-white transition-colors">Xüsusiyyətlər</a>
            <a href="#pricing" className="hidden md:inline text-zinc-400 hover:text-white transition-colors">Qiymətlər</a>
            <a href="#faq" className="hidden md:inline text-zinc-400 hover:text-white transition-colors">FAQ</a>
            <button
              data-testid="landing-login-btn"
              onClick={goToApp}
              className="px-4 sm:px-5 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors text-sm sm:text-base"
            >
              Daxil ol
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a12] via-[#0d0d18] to-[#09090b]" />
        
        {/* Background TV with Harry Potter */}
        <div className="absolute inset-0">
          <img 
            src="https://image.tmdb.org/t/p/original/fBuaReSrJdxmGzv7ChcMRMJTMHM.jpg"
            alt="Harry Potter Background"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/90 to-[#09090b]/60 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left - Text */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-400 text-xs sm:text-sm mb-4 sm:mb-6">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                Canlı yayım
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-4 sm:mb-6">
                Evin rahatlığında
                <span className="text-blue-500"> premium TV</span> təcrübəsi
              </h1>
              <p className="text-base sm:text-xl text-zinc-400 mb-6 sm:mb-8 leading-relaxed">
                200+ HD kanal, 1000+ film və serial. İstənilən cihazda, istənilən vaxt. 
                Peyk antenasız, kabelsiz, sadəcə internet.
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <button className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold text-base sm:text-lg transition-all hover:scale-105">
                  <Play className="w-5 h-5" />
                  7 gün pulsuz sına
                </button>
                <button className="flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-semibold text-base sm:text-lg transition-all">
                  Qiymətlərə bax
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-4 sm:gap-8 mt-6 sm:mt-10 text-sm sm:text-base text-zinc-500">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  Müqavilə yoxdur
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  İstənilən vaxt ləğv
                </div>
              </div>
            </div>

            {/* Right - Device Mockup from ModernTV */}
            <div className="order-1 lg:order-2 flex justify-center items-center mb-4 lg:mb-0 lg:mt-0">
              <img 
                src="https://admin.moderntv.eu/media/homepage/supported-devices.webp"
                alt="Fibernet TV on all devices"
                className="w-full max-w-md lg:max-w-none lg:w-[150%] object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Showcase */}
      <section id="features" className="py-12 sm:py-16 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-center text-zinc-500 mb-6 sm:mb-8 text-sm sm:text-base">Bütün platformalarda mövcuddur</p>
          <div className="flex items-center justify-center gap-3 sm:gap-6 flex-wrap">
            {/* App Store */}
            <a href="#" className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors text-sm sm:text-base">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <p className="text-[10px] sm:text-xs text-zinc-400">Yüklə</p>
                <p className="font-semibold text-sm sm:text-base">App Store</p>
              </div>
            </a>
            
            {/* Google Play */}
            <a href="#" className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors text-sm sm:text-base">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-zinc-400">Yüklə</p>
                <p className="font-semibold">Google Play</p>
              </div>
            </a>
            
            {/* Android TV */}
            <a href="#" className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors text-sm sm:text-base">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21,17H3V5H21M21,3H3A2,2 0 0,0 1,5V17A2,2 0 0,0 3,19H8V21H16V19H21A2,2 0 0,0 23,17V5A2,2 0 0,0 21,3Z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-zinc-400">Yüklə</p>
                <p className="font-semibold">Android TV</p>
              </div>
            </a>
            
            {/* Tizen (Samsung) */}
            <a href="#" className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors text-sm sm:text-base">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21,17H3V5H21M21,3H3A2,2 0 0,0 1,5V17A2,2 0 0,0 3,19H8V21H16V19H21A2,2 0 0,0 23,17V5A2,2 0 0,0 21,3Z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-zinc-400">Samsung</p>
                <p className="font-semibold">Tizen TV</p>
              </div>
            </a>
            
            {/* WebOS (LG) */}
            <a href="#" className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors text-sm sm:text-base">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21,17H3V5H21M21,3H3A2,2 0 0,0 1,5V17A2,2 0 0,0 3,19H8V21H16V19H21A2,2 0 0,0 23,17V5A2,2 0 0,0 21,3Z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-zinc-400">LG</p>
                <p className="font-semibold">WebOS TV</p>
              </div>
            </a>
            
            {/* Web Browser */}
            <a href="#" className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl transition-colors text-sm sm:text-base">
              <svg className="w-6 h-6 sm:w-8 sm:h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-zinc-400">İstənilən</p>
                <p className="font-semibold">Web Brauzer</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">Niyə Fibernet TV?</h2>
            <p className="text-zinc-400 text-base sm:text-lg">Ən yaxşı TV təcrübəsi üçün lazım olan hər şey</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-5 sm:p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl hover:border-blue-500/50 transition-colors group">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-blue-600/20 rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-blue-600/30 transition-colors">
                  <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-500" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-zinc-500 text-sm sm:text-base">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 sm:py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">Sadə və şəffaf qiymətlər</h2>
            <p className="text-zinc-400 text-base sm:text-lg">Sizə uyğun planı seçin</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 max-w-5xl mx-auto items-stretch">
            {plans.map((plan, i) => (
              <div 
                key={i} 
                className={`relative p-6 sm:p-8 rounded-2xl border flex flex-col ${
                  plan.popular 
                    ? 'bg-blue-600/10 border-blue-500 md:scale-105' 
                    : 'bg-zinc-900/50 border-zinc-800'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                    Ən populyar
                  </div>
                )}
                <h3 className="text-lg sm:text-xl font-semibold mb-2">{plan.name}</h3>
                <p className="text-zinc-500 mb-4 text-sm sm:text-base">{plan.channels} kanal</p>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-3xl sm:text-4xl font-bold">{plan.price}</span>
                  <span className="text-zinc-500 text-sm sm:text-base">AZN/{plan.period || 'ay'}</span>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8 flex-grow text-sm sm:text-base">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-zinc-400">
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-2.5 sm:py-3 rounded-xl font-semibold transition-colors mt-auto text-sm sm:text-base ${
                  plan.popular 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-zinc-800 hover:bg-zinc-700'
                }`}>
                  {plan.isFree ? 'Pulsuz başla' : 'Seç'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">Müştərilərimiz nə deyir?</h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 sm:p-8 bg-zinc-900/50 border border-zinc-800 rounded-2xl">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className={`w-4 h-4 sm:w-5 sm:h-5 ${j < t.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-700'}`} />
                  ))}
                </div>
                <p className="text-zinc-300 mb-4 sm:mb-6 text-sm sm:text-base">"{t.text}"</p>
                <p className="font-semibold text-sm sm:text-base">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="p-8 sm:p-12 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-3xl">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">Hazırsınız?</h2>
            <p className="text-zinc-400 text-base sm:text-lg mb-6 sm:mb-8">İndi qoşulun və 7 gün pulsuz sınayın</p>
            <button className="px-8 sm:px-10 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 rounded-xl font-semibold text-base sm:text-lg transition-all hover:scale-105">
              Pulsuz başla
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6 text-center md:text-left">
            <div className="flex items-center gap-3">
              <img src="/fibernet-logo.png" alt="Fibernet TV" className="w-8 h-8" />
              <span className="font-semibold">Fibernet TV</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-zinc-500 text-xs sm:text-sm">
              <a href="#" className="hover:text-white transition-colors">Haqqımızda</a>
              <a href="#" className="hover:text-white transition-colors">Əlaqə</a>
              <a href="#" className="hover:text-white transition-colors">Məxfilik</a>
              <a href="#" className="hover:text-white transition-colors">Şərtlər</a>
            </div>
            <p className="text-zinc-600 text-xs sm:text-sm">© 2024 Fibernet TV</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
